import type { AppDocument } from '../schema'

export interface ValidationResult {
  phase: 'schema' | 'completeness' | 'integrity'
  valid: boolean
  errors: ValidationError[]
  warnings: ValidationWarning[]
}

export interface ValidationError {
  type: string
  message: string
  entityId?: string
  entityType?: string
}

export interface ValidationWarning {
  type: string
  message: string
  entityId?: string
}

export interface MigrationValidationResult {
  valid: boolean
  phases: {
    schema: ValidationResult
    completeness: ValidationResult
    integrity: ValidationResult
  }
  summary: {
    totalEntries: number
    totalTopics: number
    totalProjects: number
    errorCount: number
    warningCount: number
  }
}

/**
 * Phase 1: Schema Validation
 * Verify all entities have required fields with correct types
 */
function validateSchema(doc: AppDocument): ValidationResult {
  const errors: ValidationError[] = []
  const warnings: ValidationWarning[] = []

  // Validate entries
  Object.entries(doc.entries).forEach(([id, entry]) => {
    if (!entry.id)
      errors.push({
        type: 'missing_id',
        message: `Entry missing id`,
        entityId: id,
        entityType: 'entry',
      })
    if (!entry.type)
      errors.push({
        type: 'missing_type',
        message: `Entry missing type`,
        entityId: id,
        entityType: 'entry',
      })
    if (!entry.title)
      errors.push({
        type: 'missing_title',
        message: `Entry missing title`,
        entityId: id,
        entityType: 'entry',
      })
    if (!entry.createdAt)
      errors.push({
        type: 'missing_createdAt',
        message: `Entry missing createdAt`,
        entityId: id,
        entityType: 'entry',
      })
    if (!Array.isArray(entry.topicIds))
      errors.push({
        type: 'invalid_topicIds',
        message: `Entry topicIds not an array`,
        entityId: id,
        entityType: 'entry',
      })
  })

  // Validate topics
  Object.entries(doc.topics).forEach(([id, topic]) => {
    if (!topic.id)
      errors.push({
        type: 'missing_id',
        message: `Topic missing id`,
        entityId: id,
        entityType: 'topic',
      })
    if (!topic.name)
      errors.push({
        type: 'missing_name',
        message: `Topic missing name`,
        entityId: id,
        entityType: 'topic',
      })
  })

  // Validate projects
  Object.entries(doc.projects).forEach(([id, project]) => {
    if (!project.id)
      errors.push({
        type: 'missing_id',
        message: `Project missing id`,
        entityId: id,
        entityType: 'project',
      })
    if (!project.name)
      errors.push({
        type: 'missing_name',
        message: `Project missing name`,
        entityId: id,
        entityType: 'project',
      })
    if (!['active', 'completed', 'on_hold'].includes(project.status)) {
      errors.push({
        type: 'invalid_status',
        message: `Project has invalid status: ${project.status}`,
        entityId: id,
        entityType: 'project',
      })
    }
  })

  return {
    phase: 'schema',
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

/**
 * Phase 2: Completeness Validation
 * Verify expected data exists (not empty after migration)
 */
function validateCompleteness(doc: AppDocument): ValidationResult {
  const errors: ValidationError[] = []
  const warnings: ValidationWarning[] = []

  const entryCount = Object.keys(doc.entries).length
  const topicCount = Object.keys(doc.topics).length

  // Warn if completely empty (possible failed migration)
  if (entryCount === 0) {
    warnings.push({
      type: 'empty_entries',
      message: 'No entries found - migration may have failed',
    })
  }

  // Check for orphaned references
  const entryIds = new Set(Object.keys(doc.entries))
  const topicIds = new Set(Object.keys(doc.topics))
  const projectIds = new Set(Object.keys(doc.projects))
  const areaIds = new Set(Object.keys(doc.areas))

  // Check entries reference valid topics
  Object.values(doc.entries).forEach((entry) => {
    entry.topicIds.forEach((topicId) => {
      if (!topicIds.has(topicId)) {
        warnings.push({
          type: 'orphaned_topic_ref',
          message: `Entry references non-existent topic: ${topicId}`,
          entityId: entry.id,
        })
      }
    })

    if (entry.projectId && !projectIds.has(entry.projectId)) {
      warnings.push({
        type: 'orphaned_project_ref',
        message: `Entry references non-existent project: ${entry.projectId}`,
        entityId: entry.id,
      })
    }

    if (entry.areaId && !areaIds.has(entry.areaId)) {
      warnings.push({
        type: 'orphaned_area_ref',
        message: `Entry references non-existent area: ${entry.areaId}`,
        entityId: entry.id,
      })
    }
  })

  return {
    phase: 'completeness',
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

/**
 * Phase 3: Referential Integrity Validation
 * Verify all references between entities are valid
 */
function validateIntegrity(doc: AppDocument): ValidationResult {
  const errors: ValidationError[] = []
  const warnings: ValidationWarning[] = []

  const entryIds = new Set(Object.keys(doc.entries))

  // Validate links reference valid entries
  Object.values(doc.links).forEach((link) => {
    if (!entryIds.has(link.sourceId)) {
      errors.push({
        type: 'invalid_link_source',
        message: `Link references non-existent source: ${link.sourceId}`,
        entityId: link.id,
        entityType: 'link',
      })
    }
    if (!entryIds.has(link.targetId)) {
      errors.push({
        type: 'invalid_link_target',
        message: `Link references non-existent target: ${link.targetId}`,
        entityId: link.id,
        entityType: 'link',
      })
    }
  })

  // Validate topic hierarchy (parentId references)
  Object.values(doc.topics).forEach((topic) => {
    if (topic.parentId && !doc.topics[topic.parentId]) {
      errors.push({
        type: 'invalid_parent_topic',
        message: `Topic references non-existent parent: ${topic.parentId}`,
        entityId: topic.id,
        entityType: 'topic',
      })
    }
  })

  // Validate project area references
  Object.values(doc.projects).forEach((project) => {
    if (project.areaId && !doc.areas[project.areaId]) {
      errors.push({
        type: 'invalid_project_area',
        message: `Project references non-existent area: ${project.areaId}`,
        entityId: project.id,
        entityType: 'project',
      })
    }
  })

  return {
    phase: 'integrity',
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

/**
 * Run full three-phase migration validation
 */
export function validateMigration(doc: AppDocument): MigrationValidationResult {
  const schemaResult = validateSchema(doc)
  const completenessResult = validateCompleteness(doc)
  const integrityResult = validateIntegrity(doc)

  const allErrors = [
    ...schemaResult.errors,
    ...completenessResult.errors,
    ...integrityResult.errors,
  ]
  const allWarnings = [
    ...schemaResult.warnings,
    ...completenessResult.warnings,
    ...integrityResult.warnings,
  ]

  return {
    valid: allErrors.length === 0,
    phases: {
      schema: schemaResult,
      completeness: completenessResult,
      integrity: integrityResult,
    },
    summary: {
      totalEntries: Object.keys(doc.entries).length,
      totalTopics: Object.keys(doc.topics).length,
      totalProjects: Object.keys(doc.projects).length,
      errorCount: allErrors.length,
      warningCount: allWarnings.length,
    },
  }
}
